import React, { useState } from "react";
import { Box, Container, TextField, Typography, Link } from "@mui/material";
import LoadingButton from "@mui/lab/LoadingButton";
import { useNavigate } from "react-router-dom";
import BASE_URL from "../../../config/apiConfig";
import "./SignUpView.css";

const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export const SignUpView = () => {
  const navigate = useNavigate();

  const [userName, setName] = useState("");
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [signupError, setSignupError] = useState("");

  const validateForm = () => {
    const newErrors = {};

    if (!userName.trim()) {
      newErrors.userName = "Name is required";
    } else if (userName.length > 20) {
      newErrors.userName = "Name must be 20 characters or less";
    }

    if (!email.trim()) {
      newErrors.email = "Email is required";
    } else if (!emailRegex.test(email)) {
      newErrors.email = "Invalid email address";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSignUp = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    setLoading(true);
    setSignupError("");

    try {
      const response = await fetch(`${BASE_URL}/api/user/auth/signup`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ userName, email }),
      });

      if (response.ok) {
        const data = await response.json();

        localStorage.setItem("email", email);
        console.log("Signup successful:", data);

        navigate("/get-otp");
      } else {
        const errorData = await response.json();
        setSignupError(errorData.message || "Signup failed");
      }
    } catch (error) {
      console.error("Error during signup:", error);
      setSignupError("Error during signup. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box className="farmology-auth-page">
      <Container className="farmology-auth-container">
        <Box className="farmology-auth-card">
          <Box className="farmology-auth-form-panel">
            <Box className="farmology-auth-brand">
              <Box className="farmology-auth-logo-wrap">
                <img
                  src="/LOGOS/Farmology-Logo-e1687720251448.png"
                  alt="Farmology"
                  className="farmology-auth-logo"
                />
              </Box>

              <Typography className="farmology-auth-eyebrow">
                SMART AGRICULTURE
              </Typography>

              <Typography component="h1" className="farmology-auth-title">
                Create your account
              </Typography>

              <Typography className="farmology-auth-description">
                Get started with Farmology and make better decisions for every
                hectare.
              </Typography>
            </Box>

            <Box
              component="form"
              onSubmit={handleSignUp}
              className="farmology-auth-form"
            >
              <TextField
                fullWidth
                name="name"
                label="Your Name"
                value={userName}
                onChange={(e) => setName(e.target.value)}
                error={!!errors.userName}
                helperText={errors.userName}
                variant="outlined"
                className="farmology-auth-field"
              />

              <TextField
                fullWidth
                name="email"
                type="email"
                label="Email Address"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                error={!!errors.email}
                helperText={errors.email}
                variant="outlined"
                className="farmology-auth-field"
              />

              {signupError && (
                <Typography className="farmology-auth-error">
                  {signupError}
                </Typography>
              )}

              <LoadingButton
                fullWidth
                size="large"
                type="submit"
                variant="contained"
                loading={loading}
                className="farmology-auth-submit"
              >
                Get OTP
                <span className="farmology-auth-arrow">↗</span>
              </LoadingButton>

              <Typography className="farmology-auth-signin">
                Already have an account?{" "}
                <Link href="/user/login" className="farmology-auth-link">
                  Sign In
                </Link>
              </Typography>
            </Box>

            <Typography className="farmology-auth-footer-note">
              © {new Date().getFullYear()} Farmology. Smart decisions for
              smarter farming.
            </Typography>
          </Box>

          <Box className="farmology-auth-visual-panel">
            <Box className="farmology-auth-visual-copy">
              <Typography className="farmology-auth-visual-eyebrow">
                FARMOLOGY PLATFORM
              </Typography>

              <Typography
                component="h2"
                className="farmology-auth-visual-title"
              >
                Better data.
                <br />
                Better farming.
              </Typography>

              <Typography className="farmology-auth-visual-text">
                Bring satellite intelligence, weather insights and practical
                farm information together in one place.
              </Typography>
            </Box>

            <Box className="farmology-auth-image-wrap">
              <img
                src="/Images/Login_Body.webp"
                alt="Farmology agriculture illustration"
                className="farmology-auth-image"
              />
            </Box>

            <Box className="farmology-auth-stat-card">
              <span className="farmology-auth-stat-label">
                FARM INTELLIGENCE
              </span>
              <strong>Built around your farm</strong>
            </Box>

            <Box className="farmology-auth-circle" />
          </Box>
        </Box>
      </Container>
    </Box>
  );
};

export default SignUpView;
